import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";

const toneStyles = {
  error: {
    icon: "!",
    iconClass: "bg-rose-100 text-rose-700",
    panelClass: "border-rose-200 bg-rose-50",
  },
  info: {
    icon: "i",
    iconClass: "bg-blue-100 text-blue-700",
    panelClass: "border-blue-200 bg-blue-50",
  },
};

export default function OrderCreationIssueModal({ issue, onClose, onAction }) {
  const [copied, setCopied] = useState(false);
  const styles = toneStyles[issue?.tone] || toneStyles.error;

  useEffect(() => {
    if (!issue) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [issue, onClose]);

  if (!issue) return null;

  const copyReference = async () => {
    if (!issue.correlationId) return;
    try {
      await navigator.clipboard.writeText(issue.correlationId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      setCopied(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-issue-title"
        aria-describedby="order-issue-description"
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start gap-4 border-b border-slate-100 p-6">
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-xl font-bold ${styles.iconClass}`}>
            {styles.icon}
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="order-issue-title" className="text-xl font-bold text-slate-900">{issue.title}</h2>
            <p id="order-issue-description" className="mt-1 text-sm leading-6 text-slate-600">{issue.message}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-5 p-6">
          {issue.details?.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold text-slate-800">Why carriers could not book this order</h3>
              <div className="space-y-2">
                {issue.details.map((detail) => (
                  <div key={`${detail.category}-${detail.label}`} className={`rounded-xl border p-3 ${styles.panelClass}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-800">{detail.label}</p>
                      <span className="shrink-0 rounded-full bg-white/80 px-2 py-0.5 text-xs font-medium text-slate-600">
                        {detail.attempts} {detail.attempts === 1 ? "attempt" : "attempts"}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-5 text-slate-600">
                      {detail.carriers.join(", ")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(issue.orderReference || issue.correlationId) && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              {issue.orderReference && (
                <p className="text-xs text-slate-600">
                  <span className="font-semibold text-slate-700">Order reference:</span>{" "}
                  <span className="font-mono">{issue.orderReference}</span>
                </p>
              )}
              {issue.correlationId && (
                <div className="mt-1 flex items-center justify-between gap-3">
                  <p className="min-w-0 truncate text-xs text-slate-500">
                    <span className="font-semibold text-slate-700">Support reference:</span>{" "}
                    <span className="font-mono">{issue.correlationId}</span>
                  </p>
                  <button
                    type="button"
                    onClick={copyReference}
                    className="shrink-0 text-xs font-semibold text-blue-700 hover:text-blue-800"
                  >
                    {copied ? "Copied" : "Copy"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* One action only — closing is the X, Escape or the backdrop. */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onAction(issue.action, issue)}
              className="rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-800"
            >
              {issue.actionLabel || "Continue"}
            </button>
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
}

